import { useState, useEffect, type FormEvent } from 'react';
import type { TransportRoute, RouteStop } from './routeMockData';
import './RouteBuilder.css';
import { useTranslation } from 'react-i18next';
import { createTransportRoute, updateTransportRoute, deleteTransportRoute, fetchTransportRoutes, addStopToRoute, fetchRouteStops, type TransportRouteRecord, type RouteStopRecord } from '../../services/transportRouteApi';
import { fetchTransportStops, createTransportStop, type TransportStopRecord } from '../../services/transportStopApi';
import { fetchVehicles, type Vehicle } from '../../services/transportFleetApi';
import { fetchStaffList, mapStaffToUi, type MappedStaff } from '../../services/staffApi';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN');
}

function mapRouteStopToLocal(r: RouteStopRecord): RouteStop {
    return {
        id: r.id,
        route_stop_id: r.id,
        transport_stop_id: r.transport_stop_id,
        stop_name: r.stop.name,
        pickup_time: r.pickup_time ? r.pickup_time.slice(0, 5) : '',
        drop_time: r.drop_time ? r.drop_time.slice(0, 5) : '',
        monthly_fee: Number(r.monthly_fee),
        order: r.order,
    };
}

function mapRouteToLocal(r: TransportRouteRecord): TransportRoute {
    return {
        id: r.id,
        name: r.title,
        vehicle_id: r.vehicle_id ?? '',
        vehicle_number: r.vehicle?.vehicle_number ?? '',
        driver_id: r.driver_id ?? '',
        description: '',
        default_monthly_fee: Number(r.default_monthly_fee ?? 0),
        is_active: r.is_active,
        students_count: r.students_count ?? 0,
        stops: [],
    };
}

export default function RouteBuilder() {
    const { t: _t } = useTranslation();
    const [routes, setRoutes] = useState<TransportRoute[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [saving, setSaving] = useState(false);

    /* Route form — shared by create and edit */
    const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
    const [formRouteId, setFormRouteId] = useState<string | null>(null);
    const [formTitle, setFormTitle] = useState('');
    const [formVehicleId, setFormVehicleId] = useState('');
    const [formDriverId, setFormDriverId] = useState('');
    const [formDefaultFee, setFormDefaultFee] = useState(0);
    const [savingRoute, setSavingRoute] = useState(false);
    const [titleError, setTitleError] = useState('');

    /* Delete route */
    const [deleteTarget, setDeleteTarget] = useState<TransportRoute | null>(null);
    const [deletingRoute, setDeletingRoute] = useState(false);

    /* Transport stops */
    const [transportStops, setTransportStops] = useState<TransportStopRecord[]>([]);
    const [showStopsPanel, setShowStopsPanel] = useState(false);
    const [stopName, setStopName] = useState('');
    const [stopLat, setStopLat] = useState('');
    const [stopLng, setStopLng] = useState('');
    const [stopNameError, setStopNameError] = useState('');
    const [creatingStop, setCreatingStop] = useState(false);

    /* Pending stop form (shown after selecting from dropdown before POST) */
    const [pendingStopId, setPendingStopId] = useState<string | null>(null);
    const [pendingPickup, setPendingPickup] = useState('07:00');
    const [pendingDrop, setPendingDrop] = useState('16:00');
    const [pendingFee, setPendingFee] = useState(0);
    const [addingStop, setAddingStop] = useState(false);

    /* Reference data */
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<MappedStaff[]>([]);

    useEffect(() => {
        let isMounted = true;
        fetchTransportRoutes().then((data) => {
            if (!isMounted) return;
            setRoutes(data.map(mapRouteToLocal));
            if (data.length > 0) setSelectedRoute(data[0].id);
        }).catch(() => { }).finally(() => { if (isMounted) setLoading(false); });
        fetchTransportStops().then((data) => { if (isMounted) setTransportStops(data); }).catch(() => { });
        fetchVehicles().then((v) => { if (isMounted) setVehicles(v); }).catch(() => { });
        fetchStaffList({ isTeacher: false }).then((res) => {
            if (!isMounted) return;
            setDrivers(res.data.map(mapStaffToUi).filter(s => s.designation === 'Driver'));
        }).catch(() => { });
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (!selectedRoute) return;
        let isMounted = true;
        fetchRouteStops(selectedRoute).then((data) => {
            if (!isMounted) return;
            setRoutes((prev) => prev.map((r) =>
                r.id !== selectedRoute ? r : { ...r, stops: data.map(mapRouteStopToLocal) }
            ));
        }).catch(() => { });
        return () => { isMounted = false; };
    }, [selectedRoute]);

    const activeRoute = routes.find((r) => r.id === selectedRoute) || null;

    /* ── Stop sequence operations ── */
    const updateStop = (stopId: string, field: keyof RouteStop, value: string | number) => {
        setRoutes((prev) => prev.map((r) =>
            r.id !== selectedRoute ? r : {
                ...r, stops: r.stops.map((s) => s.id === stopId ? { ...s, [field]: value } : s),
            }
        ));
    };

    const moveStop = (stopId: string, direction: 'up' | 'down') => {
        setRoutes((prev) => prev.map((r) => {
            if (r.id !== selectedRoute) return r;
            const stops = [...r.stops].sort((a, b) => a.order - b.order);
            const idx = stops.findIndex((s) => s.id === stopId);
            if (idx < 0) return r;
            if (direction === 'up' && idx === 0) return r;
            if (direction === 'down' && idx === stops.length - 1) return r;
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            const tempOrder = stops[idx].order;
            stops[idx] = { ...stops[idx], order: stops[swapIdx].order };
            stops[swapIdx] = { ...stops[swapIdx], order: tempOrder };
            return { ...r, stops };
        }));
    };

    const removeStop = (stopId: string) => {
        setRoutes((prev) => prev.map((r) => {
            if (r.id !== selectedRoute) return r;
            const filtered = r.stops.filter((s) => s.id !== stopId);
            const reordered = filtered.sort((a, b) => a.order - b.order).map((s, i) => ({ ...s, order: i + 1 }));
            return { ...r, stops: reordered };
        }));
    };

    const selectPendingStop = (transportStopId: string) => {
        setPendingStopId(transportStopId);
        setPendingPickup('07:00');
        setPendingDrop('16:00');
        setPendingFee(0);
    };

    const cancelPendingStop = () => {
        setPendingStopId(null);
        setPendingPickup('07:00');
        setPendingDrop('16:00');
        setPendingFee(0);
    };

    const confirmAddStop = async () => {
        const stop = transportStops.find((s) => s.id === pendingStopId);
        if (!stop || !activeRoute) return;
        setAddingStop(true);
        const maxOrder = activeRoute.stops.length > 0 ? Math.max(...activeRoute.stops.map((s) => s.order)) : 0;
        const order = maxOrder + 1;
        try {
            await addStopToRoute(activeRoute.id, {
                transport_stop_id: stop.id,
                order,
                pickup_time: pendingPickup,
                drop_time: pendingDrop,
                monthly_fee: pendingFee,
            });
            const newStop: RouteStop = {
                id: String(Date.now()), route_stop_id: String(Date.now()),
                transport_stop_id: stop.id, stop_name: stop.name,
                pickup_time: pendingPickup, drop_time: pendingDrop, monthly_fee: pendingFee, order,
            };
            setRoutes((prev) => prev.map((r) =>
                r.id !== selectedRoute ? r : { ...r, stops: [...r.stops, newStop] }
            ));
            cancelPendingStop();
            setSuccessMsg('Stop added successfully!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch {
            setErrorMsg('Failed to add stop.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setAddingStop(false);
        }
    };

    /* ── Create / edit route ── */
    const closeRouteForm = () => {
        setFormMode(null); setFormRouteId(null);
        setFormTitle(''); setFormVehicleId(''); setFormDriverId('');
        setFormDefaultFee(0); setTitleError('');
    };

    const toggleCreateForm = () => {
        if (formMode === 'create') { closeRouteForm(); return; }
        closeRouteForm();
        setFormMode('create');
    };

    const openEditForm = (route: TransportRoute) => {
        setFormMode('edit');
        setFormRouteId(route.id);
        setFormTitle(route.name);
        setFormVehicleId(route.vehicle_id);
        setFormDriverId(route.driver_id);
        setFormDefaultFee(route.default_monthly_fee);
        setTitleError('');
    };

    const handleRouteSubmit = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        const trimmed = formTitle.trim();
        if (!trimmed) { setTitleError('Route title is required.'); return; }
        if (trimmed.length > 100) { setTitleError('Title must be 100 characters or less.'); return; }
        const isEdit = formMode === 'edit' && formRouteId !== null;
        const payload = {
            title: trimmed,
            vehicle_id: formVehicleId || null,
            driver_id: formDriverId || null,
            default_monthly_fee: formDefaultFee,
            // Not editable in the UI: new routes are active, edits keep the server's value.
            is_active: isEdit ? (routes.find(r => r.id === formRouteId)?.is_active ?? true) : true,
        };
        setSavingRoute(true);
        try {
            if (isEdit) {
                const record = await updateTransportRoute(formRouteId, payload);
                const updated = mapRouteToLocal(record);
                // The update response carries no stops or students_count — keep what's already loaded.
                setRoutes(prev => prev.map(r => r.id !== formRouteId ? r : { ...updated, stops: r.stops, students_count: r.students_count }));
                setSuccessMsg('Route updated successfully!');
            } else {
                const record = await createTransportRoute(payload);
                const created = mapRouteToLocal(record);
                setRoutes(prev => [...prev, created]);
                setSelectedRoute(created.id);
                setSuccessMsg('Route created successfully!');
            }
            closeRouteForm();
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch {
            setErrorMsg(isEdit ? 'Failed to update route.' : 'Failed to create route.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setSavingRoute(false);
        }
    };

    /* ── Delete route ── */
    const confirmDeleteRoute = async () => {
        if (!deleteTarget || deletingRoute) return;
        const targetId = deleteTarget.id;
        setDeletingRoute(true);
        try {
            await deleteTransportRoute(targetId);
            const remaining = routes.filter(r => r.id !== targetId);
            setRoutes(remaining);
            if (selectedRoute === targetId) setSelectedRoute(remaining[0]?.id ?? null);
            if (formRouteId === targetId) closeRouteForm();
            setDeleteTarget(null);
            setSuccessMsg('Route deleted successfully!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch {
            setErrorMsg('Failed to delete route.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setDeletingRoute(false);
        }
    };

    /* ── Create transport stop ── */
    const handleCreateStop = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        const trimmed = stopName.trim();
        if (!trimmed) { setStopNameError('Stop name is required.'); return; }
        if (trimmed.length > 100) { setStopNameError('Name must be 100 characters or less.'); return; }
        setCreatingStop(true);
        try {
            await createTransportStop({
                name: trimmed,
                latitude: stopLat ? Number(stopLat) : null,
                longitude: stopLng ? Number(stopLng) : null,
            });
            const newRecord: TransportStopRecord = {
                id: String(Date.now()),
                organization_id: '',
                name: trimmed,
                latitude: stopLat ? Number(stopLat) : null,
                longitude: stopLng ? Number(stopLng) : null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            };
            setTransportStops(prev => [...prev, newRecord]);
            setStopName(''); setStopLat(''); setStopLng(''); setStopNameError('');
            setSuccessMsg('Transport stop created!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch {
            setErrorMsg('Failed to create stop.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setCreatingStop(false);
        }
    };

    /* ── Save stops (local) ── */
    const handleSave = () => {
        if (!activeRoute || saving) return;
        setSaving(true);
        setSuccessMsg('Route saved successfully!');
        setTimeout(() => { setSuccessMsg(''); setSaving(false); }, 5000);
    };

    const sortedStops = activeRoute ? [...activeRoute.stops].sort((a, b) => a.order - b.order) : [];
    const rightPaneDesc = [
        activeRoute?.description,
        activeRoute?.vehicle_number,
        activeRoute ? `Default ${formatCurrency(activeRoute.default_monthly_fee)} / mo` : '',
    ].filter(Boolean).join(' · ');

    const renderRouteForm = () => (
        <form className="routes__create-form" onSubmit={handleRouteSubmit} noValidate>
            <div className="routes__create-field">
                <label className="routes__create-label" htmlFor="rcTitle">Title <span className="routes__create-req">*</span></label>
                <input
                    className={`routes__create-input ${titleError ? 'routes__create-input--error' : ''}`}
                    id="rcTitle" type="text" placeholder="e.g. Route A — North"
                    value={formTitle} maxLength={100}
                    onChange={(e) => { setFormTitle(e.target.value); if (titleError) setTitleError(''); }}
                    autoFocus
                />
                {titleError && <p className="routes__create-error">{titleError}</p>}
            </div>
            <div className="routes__create-field">
                <label className="routes__create-label" htmlFor="rcVehicle">Vehicle</label>
                <select className="routes__create-select" id="rcVehicle" value={formVehicleId} onChange={(e) => setFormVehicleId(e.target.value)}>
                    <option value="">None</option>
                    {vehicles.map(v => (
                        <option key={v.id} value={v.id}>{v.vehicle_number}{v.model ? ` — ${v.model}` : ''}</option>
                    ))}
                </select>
            </div>
            <div className="routes__create-field">
                <label className="routes__create-label" htmlFor="rcDriver">Driver</label>
                <select className="routes__create-select" id="rcDriver" value={formDriverId} onChange={(e) => setFormDriverId(e.target.value)}>
                    <option value="">None</option>
                    {drivers.map(d => (
                        <option key={d.user_id} value={d.user_id}>{d.first_name} {d.last_name}</option>
                    ))}
                </select>
            </div>
            <div className="routes__create-field">
                <label className="routes__create-label" htmlFor="rcFee">Default Monthly Fee (₹)</label>
                <input
                    className="routes__create-input"
                    id="rcFee" type="number" min={0} placeholder="0"
                    value={formDefaultFee}
                    onChange={(e) => setFormDefaultFee(Number(e.target.value))}
                />
            </div>
            <div className="routes__create-actions">
                <button className="routes__create-submit" type="submit" disabled={savingRoute}>
                    {formMode === 'edit'
                        ? (savingRoute ? 'Saving…' : 'Save Changes')
                        : (savingRoute ? 'Creating…' : 'Create Route')}
                </button>
                <button className="routes__create-cancel" type="button" onClick={closeRouteForm} disabled={savingRoute}>Cancel</button>
            </div>
        </form>
    );

    if (loading) {
        return <div className="routes"><div className="routes__loading"><div className="routes__loader"><span className="routes__loader-dot" /><span className="routes__loader-dot" /><span className="routes__loader-dot" /></div></div></div>;
    }

    return (
        <div className="routes">
            {successMsg && (
                <div className={`routes__toast`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="routes__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && (
                <div className={`routes__toast routes__toast--error`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{errorMsg}</span>
                    <button className="routes__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="routes__header">
                <div className="routes__header-text">
                    <h1 className="routes__title">Route Builder</h1>
                    <p className="routes__subtitle">Configure routes and stop sequences.</p>
                </div>
                <button
                    className={`routes__manage-stops-btn ${showStopsPanel ? 'routes__manage-stops-btn--active' : ''}`}
                    onClick={() => setShowStopsPanel(prev => !prev)}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3" /><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" /></svg>
                    Manage Stops
                </button>
            </div>

            {/* Transport stops panel */}
            {showStopsPanel && (
                <div className="routes__stops-panel">
                    <div className="routes__stops-panel-header">
                        <h3 className="routes__stops-panel-title">Transport Stops</h3>
                        <span className="routes__stops-panel-count">{transportStops.length} stops</span>
                    </div>
                    <div className="routes__stops-panel-body">
                        <form className="routes__stop-create" onSubmit={handleCreateStop} noValidate>
                            <div className="routes__stop-create-row">
                                <div className="routes__stop-create-field routes__stop-create-field--name">
                                    <label className="routes__create-label" htmlFor="tsName">Name <span className="routes__create-req">*</span></label>
                                    <input
                                        className={`routes__create-input ${stopNameError ? 'routes__create-input--error' : ''}`}
                                        id="tsName" type="text" placeholder="e.g. MG Road"
                                        value={stopName} maxLength={100}
                                        onChange={(e) => { setStopName(e.target.value); if (stopNameError) setStopNameError(''); }}
                                    />
                                    {stopNameError && <p className="routes__create-error">{stopNameError}</p>}
                                </div>
                                <div className="routes__stop-create-field routes__stop-create-field--coord">
                                    <label className="routes__create-label" htmlFor="tsLat">Latitude</label>
                                    <input
                                        className="routes__create-input"
                                        id="tsLat" type="number" step="any" min={-90} max={90}
                                        placeholder="12.9716"
                                        value={stopLat}
                                        onChange={(e) => setStopLat(e.target.value)}
                                    />
                                </div>
                                <div className="routes__stop-create-field routes__stop-create-field--coord">
                                    <label className="routes__create-label" htmlFor="tsLng">Longitude</label>
                                    <input
                                        className="routes__create-input"
                                        id="tsLng" type="number" step="any" min={-180} max={180}
                                        placeholder="77.5946"
                                        value={stopLng}
                                        onChange={(e) => setStopLng(e.target.value)}
                                    />
                                </div>
                                <button className="routes__stop-create-btn" type="submit" disabled={creatingStop}>
                                    {creatingStop ? (
                                        <svg className="routes__stop-create-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" stroke="currentColor" opacity=".3" /><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                                    ) : (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                            Add
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                        {transportStops.length > 0 && (
                            <div className="routes__stops-list">
                                {transportStops.map(s => (
                                    <div className="routes__stops-list-item" key={s.id}>
                                        <svg className="routes__stops-list-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3" /><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" /></svg>
                                        <span className="routes__stops-list-name">{s.name}</span>
                                        {(s.latitude != null && s.longitude != null) && (
                                            <span className="routes__stops-list-coords">{s.latitude}, {s.longitude}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {transportStops.length === 0 && (
                            <p className="routes__stops-list-empty">No transport stops yet. Add one above.</p>
                        )}
                    </div>
                </div>
            )}

            <div className="routes__split">
                {/* Left pane — routes list */}
                <div className="routes__left">
                    <div className="routes__left-header">
                        <h3 className="routes__section-heading">Routes</h3>
                        <button
                            className={`routes__new-route-btn ${formMode === 'create' ? 'routes__new-route-btn--active' : ''}`}
                            onClick={toggleCreateForm}
                            title={formMode === 'create' ? 'Close' : 'New route'}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        </button>
                    </div>

                    {formMode === 'create' && renderRouteForm()}

                    <div className="routes__route-list">
                        {routes.length === 0 && formMode !== 'create' && (
                            <div className="routes__empty-list">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 12h4l3-9 4 18 3-9h4" />
                                </svg>
                                <p className="routes__empty-list-title">No routes yet</p>
                                <span className="routes__empty-list-hint">Click <strong>+</strong> to create your first route</span>
                            </div>
                        )}
                        {routes.map((r) => (
                            <button
                                key={r.id}
                                className={`routes__route-item ${selectedRoute === r.id ? 'routes__route-item--active' : ''}`}
                                onClick={() => { setSelectedRoute(r.id); if (formMode === 'edit') closeRouteForm(); }}
                            >
                                <div className="routes__route-main">
                                    <div className="routes__route-heading">
                                        <span className="routes__route-name">{r.name}</span>
                                        <span className="routes__route-fee">{formatCurrency(r.default_monthly_fee)} / mo</span>
                                    </div>
                                </div>
                                <div className="routes__route-meta">
                                    {r.vehicle_number && <span className="routes__route-vehicle">{r.vehicle_number}</span>}
                                    <span className="routes__route-stops">{r.stops.length} stops</span>
                                    <span className="routes__route-students">{r.students_count} students</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right pane — sequence builder */}
                <div className="routes__right">
                    {activeRoute ? (
                        <>
                            <div className="routes__right-header">
                                <div>
                                    <h3 className="routes__right-title">{activeRoute.name}</h3>
                                    {rightPaneDesc && <p className="routes__right-desc">{rightPaneDesc}</p>}
                                </div>
                                <div className="routes__right-actions">
                                    <select className="routes__add-stop-select" value="" onChange={(e) => { if (e.target.value) selectPendingStop(e.target.value); }}>
                                        <option value="" disabled>+ Add stop…</option>
                                        {transportStops.filter((s) => !activeRoute.stops.some((rs) => rs.transport_stop_id === s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                    <button
                                        className={`routes__icon-btn ${formMode === 'edit' ? 'routes__icon-btn--active' : ''}`}
                                        onClick={() => { if (formMode === 'edit') closeRouteForm(); else openEditForm(activeRoute); }}
                                        title="Edit route"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    <button
                                        className="routes__icon-btn routes__icon-btn--danger"
                                        onClick={() => setDeleteTarget(activeRoute)}
                                        title="Delete route"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                    </button>
                                    <button className="routes__save-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Route'}</button>
                                </div>
                            </div>

                            {/* Edit form — appears under the header for the selected route */}
                            {formMode === 'edit' && formRouteId === activeRoute.id && (
                                <div className="routes__edit-form-wrap">{renderRouteForm()}</div>
                            )}

                            {/* Pending stop form — appears after selecting from dropdown */}
                            {pendingStopId && (() => {
                                const pendingStop = transportStops.find(s => s.id === pendingStopId);
                                if (!pendingStop) return null;
                                return (
                                    <div className="routes__pending-stop">
                                        <div className="routes__pending-stop-header">
                                            <svg className="routes__pending-stop-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3" /><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" /></svg>
                                            <span className="routes__pending-stop-name">{pendingStop.name}</span>
                                        </div>
                                        <div className="routes__pending-stop-fields">
                                            <div className="routes__pending-stop-field">
                                                <label className="routes__stop-label" htmlFor="pendPickup">Pickup Time</label>
                                                <input className="routes__stop-time" type="time" id="pendPickup" value={pendingPickup} onChange={(e) => setPendingPickup(e.target.value)} />
                                            </div>
                                            <div className="routes__pending-stop-field">
                                                <label className="routes__stop-label" htmlFor="pendDrop">Drop Time</label>
                                                <input className="routes__stop-time" type="time" id="pendDrop" value={pendingDrop} onChange={(e) => setPendingDrop(e.target.value)} />
                                            </div>
                                            <div className="routes__pending-stop-field">
                                                <label className="routes__stop-label" htmlFor="pendFee">Monthly Fee</label>
                                                <div className="routes__stop-fee-wrap">
                                                    <span className="routes__stop-currency">₹</span>
                                                    <input className="routes__stop-fee" type="number" id="pendFee" min={0} value={pendingFee} onChange={(e) => setPendingFee(Number(e.target.value))} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="routes__pending-stop-actions">
                                            <button className="routes__pending-stop-add" onClick={confirmAddStop} disabled={addingStop}>
                                                {addingStop ? (
                                                    <><svg className="routes__stop-create-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" stroke="currentColor" opacity=".3" /><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>Adding…</>
                                                ) : (
                                                    <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>Add Stop</>
                                                )}
                                            </button>
                                            <button className="routes__pending-stop-cancel" onClick={cancelPendingStop} disabled={addingStop}>Cancel</button>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="routes__sequence">
                                {sortedStops.length === 0 ? (
                                    <div className="routes__sequence-empty">
                                        {transportStops.length === 0
                                            ? 'No transport stops available. Use "Manage Stops" above to create stops first.'
                                            : 'No stops added. Use the dropdown above to add stops.'}
                                    </div>
                                ) : (
                                    sortedStops.map((stop, idx) => (
                                        <div className="routes__stop-card" key={stop.id}>
                                            <div className="routes__stop-order">
                                                <button className="routes__order-btn" disabled={idx === 0} onClick={() => moveStop(stop.id, 'up')} title="Move up">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg>
                                                </button>
                                                <span className="routes__order-num">{stop.order}</span>
                                                <button className="routes__order-btn" disabled={idx === sortedStops.length - 1} onClick={() => moveStop(stop.id, 'down')} title="Move down">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                                                </button>
                                            </div>
                                            <div className="routes__stop-content">
                                                <div className="routes__stop-name">{stop.stop_name}</div>
                                                <div className="routes__stop-fields">
                                                    <div className="routes__stop-field">
                                                        <label className="routes__stop-label">Pickup</label>
                                                        <input className="routes__stop-time" type="time" value={stop.pickup_time} onChange={(e) => updateStop(stop.id, 'pickup_time', e.target.value)} />
                                                    </div>
                                                    <div className="routes__stop-field">
                                                        <label className="routes__stop-label">Drop</label>
                                                        <input className="routes__stop-time" type="time" value={stop.drop_time} onChange={(e) => updateStop(stop.id, 'drop_time', e.target.value)} />
                                                    </div>
                                                    <div className="routes__stop-field">
                                                        <label className="routes__stop-label">Fee / mo</label>
                                                        <div className="routes__stop-fee-wrap">
                                                            <span className="routes__stop-currency">₹</span>
                                                            <input className="routes__stop-fee" type="number" min={0} value={stop.monthly_fee} onChange={(e) => updateStop(stop.id, 'monthly_fee', Number(e.target.value))} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <button className="routes__stop-remove" onClick={() => removeStop(stop.id)} title="Remove stop">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {sortedStops.length > 0 && (
                                <div className="routes__total-bar">
                                    <span className="routes__total-label">{sortedStops.length} Stops</span>
                                    <span className="routes__total-fee">Total: {formatCurrency(sortedStops.reduce((s, st) => s + st.monthly_fee, 0))} / mo</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="routes__empty-right">
                            <svg className="routes__empty-right-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 12h4l3-9 4 18 3-9h4" />
                            </svg>
                            <p className="routes__empty-right-title">
                                {routes.length === 0 ? 'Create a route to get started' : 'Select a route'}
                            </p>
                            <span className="routes__empty-right-desc">
                                {routes.length === 0
                                    ? 'Use the + button in the left panel to create your first route, then add stops here.'
                                    : 'Choose a route from the left panel to configure its stop sequence.'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {deleteTarget && (
                <div className="routes__confirm-overlay" onClick={() => { if (!deletingRoute) setDeleteTarget(null); }}>
                    <div className="routes__confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="routes__confirm-title">Delete Route</h3>
                        <p className="routes__confirm-text">
                            Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                            {deleteTarget.stops.length > 0 && ` Its ${deleteTarget.stops.length} stop${deleteTarget.stops.length === 1 ? '' : 's'} will be removed from the route.`}
                        </p>
                        <div className="routes__confirm-actions">
                            <button className="routes__confirm-cancel" onClick={() => setDeleteTarget(null)} disabled={deletingRoute}>Cancel</button>
                            <button className="routes__confirm-delete" onClick={confirmDeleteRoute} disabled={deletingRoute}>
                                {deletingRoute ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
